import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { SiteSummary } from '../models/cms-models';
import { SiteService } from './site-service';

const ACTIVE_SITE_STORAGE_KEY = 'southbound-cms.active-site-id';

@Injectable({
  providedIn: 'root',
})
export class SiteContextService {
  private siteService = inject(SiteService);
  private sitesSubject = new BehaviorSubject<SiteSummary[]>([]);
  private activeSiteIdSubject = new BehaviorSubject<string | null>(readStoredActiveSiteId());
  private loadPromise: Promise<SiteSummary[]> | null = null;

  readonly sites$ = this.sitesSubject.asObservable();
  readonly activeSiteId$ = this.activeSiteIdSubject.asObservable();
  readonly activeSite$ = combineLatest([this.sites$, this.activeSiteId$]).pipe(
    map(([sites, activeSiteId]) => sites.find((site) => site.id === activeSiteId) ?? null),
  );

  get sites(): SiteSummary[] {
    return this.sitesSubject.value;
  }

  get activeSiteId(): string | null {
    return this.activeSiteIdSubject.value;
  }

  get activeSite(): SiteSummary | null {
    return this.sites.find((site) => site.id === this.activeSiteId) ?? null;
  }

  async ensureLoaded(): Promise<SiteSummary[]> {
    if (this.sites.length) {
      this.ensureValidActiveSite();
      return this.sites;
    }

    return this.refreshSites();
  }

  async refreshSites(preferredSiteId: string | null = null): Promise<SiteSummary[]> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this.siteService
      .getSites()
      .then((sites) => {
        this.sitesSubject.next(sites);
        this.ensureValidActiveSite(preferredSiteId);
        return sites;
      })
      .finally(() => {
        this.loadPromise = null;
      });

    return this.loadPromise;
  }

  ensureValidActiveSite(preferredSiteId: string | null = null): string | null {
    const sites = this.sites;
    if (!sites.length) {
      this.persistActiveSiteId(null);
      return null;
    }

    const candidateSiteIds = [
      preferredSiteId,
      this.activeSiteIdSubject.value,
      readStoredActiveSiteId(),
      sites[0]?.id ?? null,
    ];
    const nextSiteId =
      candidateSiteIds.find((siteId) => !!siteId && sites.some((site) => site.id === siteId)) ?? sites[0].id;

    this.persistActiveSiteId(nextSiteId);
    return nextSiteId;
  }

  setActiveSite(siteId: string | null): void {
    if (!siteId) {
      this.ensureValidActiveSite();
      return;
    }

    if (this.sites.length && !this.sites.some((site) => site.id === siteId)) {
      return;
    }

    this.persistActiveSiteId(siteId);
  }

  upsertSite(site: SiteSummary, options: { setActive?: boolean } = {}): void {
    const nextSites = this.sites.some((existingSite) => existingSite.id === site.id)
      ? this.sites.map((existingSite) => (existingSite.id === site.id ? site : existingSite))
      : [...this.sites, site];

    this.sitesSubject.next(nextSites);

    if (options.setActive) {
      this.persistActiveSiteId(site.id);
      return;
    }

    this.ensureValidActiveSite();
  }

  removeSite(siteId: string): void {
    this.sitesSubject.next(this.sites.filter((site) => site.id !== siteId));
    this.ensureValidActiveSite(this.activeSiteId === siteId ? null : this.activeSiteId);
  }

  clearSites(): void {
    this.sitesSubject.next([]);
    this.activeSiteIdSubject.next(readStoredActiveSiteId());
  }

  private persistActiveSiteId(siteId: string | null): void {
    if (this.activeSiteIdSubject.value === siteId) {
      return;
    }

    if (siteId) {
      localStorage.setItem(ACTIVE_SITE_STORAGE_KEY, siteId);
    } else {
      localStorage.removeItem(ACTIVE_SITE_STORAGE_KEY);
    }

    this.activeSiteIdSubject.next(siteId);
  }
}

function readStoredActiveSiteId(): string | null {
  return localStorage.getItem(ACTIVE_SITE_STORAGE_KEY);
}
